import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import { School } from "@backend-types";
import { UsersManagementProps } from "../../../../pages/v2/UsersPage";
import { Box, Typography, Grid } from "@mui/material";
import { ReactComponent as IconSchool } from "../../../../assets/MACS-UserMangmt-Icon-School.svg";
import { SchoolDeletePrompt } from "../utils/SchoolDeletePrompt";
import { EditSchool } from "../EditDetails/EditSchool";
import { ViewSchool } from "../ViewDetails/ViewSchool";
import { SettingsButton } from "../utils/SettingsButton";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
  },
  gridRow: {
    padding: "0rem 28px",
    minHeight: "86px",
    borderTop: "#E8E8E8 1px solid",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  rowIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  gridItem: {
    display: "flex",
    alignItems: "center",
  },

  stackedData: {
    display: "flex",
    flexDirection: "column",
  },
  fieldData: {
    fontWeight: 500,
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
  fieldName: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  editColumn: {
    display: "flex",
    alignItems: "center",
  },
  deleteUserButton: {
    borderColor: "#8C1D40",
    backgroundColor: "#8C1D40",
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "2.25rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: "#701733",
    },
  },
}));

export const SchoolsTable: React.FunctionComponent<UsersManagementProps> = (props) => {
  const classes = useStyles();
  const [promptOpen, setPromptOpen] = useState(false);
  const [deleteSchool, setDeleteSchool] = useState<School | undefined>();
  const [viewOpen, setViewOpen] = useState(false);
  const [viewSchool, setViewSchool] = useState<School | undefined>();
  const [editOpen, setEditOpen] = useState(false);
  const [editSchool, setEditSchool] = useState<School | undefined>();

  const handlePromptClose = () => {
    setPromptOpen(false);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleViewClose = () => {
    setViewOpen(false);
  };

  const handleDeleteClick = (school: School | undefined) => {
    setDeleteSchool(school);
    setPromptOpen(true);
  };

  const handleEditClick = (school: School | undefined) => {
    setEditSchool(school);
    setViewOpen(false);
    setEditOpen(true);
  };

  const handleViewClick = (school: School | undefined) => {
    setViewSchool(school);
    setViewOpen(true);
  };

  return (
    <>
      <div className={classes.root}>
        <Box sx={{ width: "100%" }}>
          {props.schools?.map((school: School) => {
            const actions = [
              {
                label: "Edit",
                color: "#747474",
                onClick: () => handleEditClick(school),
                visible: true,
                isDisabled: false,
              },
              {
                label: "View",
                color: "#747474",
                onClick: () => handleViewClick(school),
                visible: true,
                isDisabled: false,
              },
              {
                label: "Delete",
                color: "#CC2F2F",
                onClick: () => handleDeleteClick(school),
                visible: false,
                isDisabled: false,
              },
            ];
            return (
              <Grid
                container
                sx={{ width: "100%" }}
                key={school.id}
                className={classes.gridRow}>
                <Grid item xs={5} className={classes.gridItem}>
                  <Box className={classes.rowIcon}>
                    <IconSchool style={{ width: 48, height: 48 }} />
                  </Box>
                  <Box className={classes.stackedData} sx={{ marginLeft: "28px" }}>
                    <Typography className={classes.fieldData}>{school.name}</Typography>
                    <Typography className={classes.fieldName}>School</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} className={classes.gridItem}>
                  <Box className={classes.stackedData}>
                    {school.district && (
                      <>
                        <Typography className={classes.fieldData}>
                          {school.district || "\u00A0"}
                        </Typography>
                        <Typography className={classes.fieldName}>District</Typography>
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={1} className={classes.gridItem}>
                  <Box sx={{ marginLeft: "auto" }}>
                    <SettingsButton
                      label="Edit"
                      sectionName={school.name}
                      actions={actions}
                    />
                  </Box>
                </Grid>
              </Grid>
            );
          })}
        </Box>
        <SchoolDeletePrompt
          promptOpen={promptOpen}
          promptOnClose={handlePromptClose}
          currentUser={props.currentUser}
          school={deleteSchool}
          onDelete={props.onDelete}
        />
        <EditSchool
          open={editOpen}
          onClose={handleEditClose}
          onSubmit={props.onDelete}
          currentUser={props.currentUser}
          school={editSchool}
        />
        <ViewSchool
          open={viewOpen}
          onClose={handleViewClose}
          onEdit={() => handleEditClick(viewSchool)}
          currentUser={props.currentUser}
          school={viewSchool}
        />
      </div>
    </>
  );
};
